// utils/gtm-events.ts

import { useAppStore } from "@/store/appStore";
import { extractUTMFromURL, getUTMCookie } from "@/lib/utils/utm-tracker";
import type { AppData } from "@/store/appStore";
import { clientLogger } from "@/lib/client/logger";
import { pushCleverTapProfile } from "@/lib/utils/clevertap-api";

const getUserId = (): string | null => {
	const store = useAppStore.getState().data;

	// Authorized user
	if (store.user?.id) {
		return store.user.id;
	}

	// Guest user from store → sync to localStorage
	if (store.user?.guestUserId) {
		localStorage.setItem("guest_user_id", store.user.guestUserId);
		return store.user.guestUserId;
	}

	// Guest fallback → survives reloads, hydration, route changes
	const storedGuestId = localStorage.getItem("guest_user_id");
	if (storedGuestId) return storedGuestId;

	return null;
};

export const getUTMFromURL = (): Record<
	string,
	string | number | boolean | string[] | null
> => {
	const utmData = extractUTMFromURL();

	if (!utmData) return {}; // No UTM in URL

	return {
		utm_source: utmData.utm_source || "(direct)",
		utm_medium: utmData.utm_medium || "(none)",
		utm_campaign: utmData.utm_campaign || "(not set)",
		utm_term: utmData.utm_term || "",
		utm_content: utmData.utm_content || "",
		referrer: utmData.referrer || "",
		landing_page: utmData.landing_page || "",
	};
};

// Helper to get current UTM from cookie (similar to getUTMFromURL)
export const getUTMFromCookie = (): Record<
	string,
	string | number | boolean | string[] | null
> => {
	const cookieUTM = getUTMCookie();

	if (!cookieUTM) return {}; // No UTM in cookie

	return {
		utm_source: cookieUTM.utm_source || "(direct)",
		utm_medium: cookieUTM.utm_medium || "(none)",
		utm_campaign: cookieUTM.utm_campaign || "(not set)",
		utm_term: cookieUTM.utm_term || "",
		utm_content: cookieUTM.utm_content || "",
		referrer: cookieUTM.referrer || "",
		landing_page: cookieUTM.landing_page || "",
	};
};

const isAuthorized = (): boolean => {
	const user = useAppStore.getState().data.user;
	return !!user?.id;
};

// Derive company name from the domain part of an employment email
// (e.g. "john@acme.com" → "Acme"). Mirrors deriveCompanyName used in
// profile/Callback/EligibilityCapsPopup.
const deriveCompanyName = (email?: string | null): string => {
	if (!email) return "";
	const match = email.match(/@([^.]+)/);
	if (!match?.[1]) return "";
	return match[1]
		.split(/[\s._-]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

// Helper to format UTM data as compact string
const formatUTMString = (
	utmData: {
		utm_source?: string;
		utm_medium?: string;
		utm_campaign?: string;
	} | null,
): string | null => {
	if (!utmData) return null;

	const { utm_source, utm_medium, utm_campaign } = utmData;
	if (!utm_source) return null;

	const parts = [utm_source, utm_medium || "", utm_campaign || ""].filter(
		Boolean,
	);
	return parts.join("/");
};

export const syncUserProperties = (
	appData: AppData,
	isNewLogin = false,
): void => {
	if (typeof window === "undefined") return;
	if (!appData) return;

	const isAuthorized = !!appData?.user?.id;

	const userProperties: Record<string, string | null> = {
		user_ctc: appData?.ctc?.toString() || null,
	};

	if (isAuthorized && appData.user) {
		const firstTouch = appData.user.firstTouchUtm;
		userProperties.first_touch_utm = firstTouch
			? formatUTMString({
					utm_source: firstTouch.utm_source,
					utm_medium: firstTouch.utm_medium,
					utm_campaign: firstTouch.utm_campaign,
				})
			: null;

		const cookieUTM = getUTMCookie();
		userProperties.last_touch_utm = cookieUTM
			? formatUTMString({
					utm_source: cookieUTM.utm_source,
					utm_medium: cookieUTM.utm_medium,
					utm_campaign: cookieUTM.utm_campaign,
				})
			: null;
	}
	window.dataLayer = window.dataLayer || [];

	//  Use 'set' command for user properties
	window.dataLayer.push({
		event: "set",
		user_ctc: userProperties.user_ctc,
		first_touch_utm: userProperties.first_touch_utm,
		last_touch_utm: userProperties.last_touch_utm,
	});

	// Push properties to CleverTap via Web SDK
	if (window.clevertap) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const cleverTapProfile: Record<string, any> = {};

		if (userProperties.user_ctc) {
			cleverTapProfile.user_ctc = userProperties.user_ctc;
		}

		if (isAuthorized && appData.user) {
			cleverTapProfile.Identity = appData.user.id;

			const fullName = [appData.user.firstName, appData.user.lastName]
				.filter(Boolean)
				.join(" ");
			if (fullName) {
				cleverTapProfile.Name = fullName;
			}

			if (appData.user.email) {
				cleverTapProfile.Email = appData.user.email;
			}
			if (appData.user.mobileNo) {
				cleverTapProfile.Phone = `+91${appData.user.mobileNo}`;
			}
			const company = deriveCompanyName(appData.user.employment?.email);
			if (company) {
				cleverTapProfile.Company = company;
			}
		}

		if (Object.keys(cleverTapProfile).length > 0) {
			if (isNewLogin) {
				// New user on this device — creates separate profile, prevents merging
				window.clevertap.onUserLogin.push({ Site: cleverTapProfile });
				if (process.env.NODE_ENV === "development") {
					console.log("CleverTap onUserLogin.push fired", cleverTapProfile);
				}
			} else {
				window.clevertap.profile.push({ Site: cleverTapProfile });
				if (process.env.NODE_ENV === "development") {
					console.log("CleverTap profile.push fired", cleverTapProfile);
				}
			}
		}
	}

	// Push nested UTM objects to CleverTap via REST API
	if (isAuthorized && appData.user) {
		const nestedProfileData: Record<string, unknown> = {};
		const firstTouch = appData.user.firstTouchUtm;
		if (firstTouch) {
			nestedProfileData.first_touch = {
				utm_source: firstTouch.utm_source || "",
				utm_medium: firstTouch.utm_medium || "",
				utm_campaign: firstTouch.utm_campaign || "",
				utm_content: firstTouch.utm_content || "",
				utm_term: firstTouch.utm_term || "",
				landing_page: firstTouch.landing_page || "",
				timestamp: firstTouch.timestamp || "",
			};
		}
		const cookieUTM = getUTMCookie();
		const lastTouchUtm = cookieUTM || appData.user.lastTouchUtm;
		if (lastTouchUtm) {
			nestedProfileData.last_touch = {
				utm_source: lastTouchUtm.utm_source || "",
				utm_medium: lastTouchUtm.utm_medium || "",
				utm_campaign: lastTouchUtm.utm_campaign || "",
				utm_content: lastTouchUtm.utm_content || "",
				utm_term: lastTouchUtm.utm_term || "",
				landing_page: lastTouchUtm.landing_page || "",
				timestamp: lastTouchUtm.timestamp || "",
			};
		}
		if (Object.keys(nestedProfileData).length > 0) {
			pushCleverTapProfile({
				identity: appData.user.id,
				profileData: nestedProfileData,
			});
		}
	}

	if (process.env.NODE_ENV === "development") {
		console.log("GTM User Properties Synced:", userProperties);
		if (window.clevertap) {
			console.log("CleverTap Profile Synced (SDK)");
		} else {
			console.log("CleverTap not loaded, SDK profile sync skipped");
		}
	}
};

export enum GTMEvent {
	// Auth
	LOGIN = "login",
	SIGNUP = "signup",

	// Onboarding
	ONBOARDING_VISITED = "onboarding_visited",
	EMAIL_VERIFICATION_SKIPPED = "email_verification_skipped",
	MOBILE_VERIFIED = "mobile_verified",
	MOBILE_VERIFICATION_SKIPPED = "mobile_verification_skipped",
	CTC_UPDATED = "ctc_updated",
	CTC_SKIPPED = "ctc_skipped",
	DEVICE_SELECTION = "device_selection",
	NAME_UPDATED = "name_updated",
	CONSENT_ACCEPTED = "consent_accepted",

	// Pages
	HOMEPAGE_VISITED = "homepage_visited",
	PROFILE_PAGE_VISITED = "profile_page_visited",
	PRODUCT_LISTING_PAGE_VISITED = "product_listing_page_visited",
	PRODUCT_DETAILS_PAGE_VISITED = "product_details_page_visited",

	// Actions
	LOCATION_CHANGED = "location_changed",
	VARIANT_CHANGED = "variant_changed",
	ACCESSORY_ADDED = "accessory_added",
	VIDEO_WATCHED = "video_watched",

	// Orders
	ORDER_INITIATED = "order_initiated",
	EMPLOYEE_ID_UPDATED = "employee_id_updated",
	PAYMENT_DONE = "payment_done",
	ORDER_COMPLETED = "order_completed",

	// Profile
	ADDRESS_UPDATED = "address_updated",
	ACCOUNT_DELETED = "account_deleted",
	MOBILE_UPDATED = "mobile_updated",

	// Support
	EMAIL_SUPPORT_CLICKED = "email_support_clicked",
	CALLBACK_REQUESTED = "callback_requested",

	// Legal
	TERMS_OF_USE_VISITED = "terms_of_use_visited",
	SHIPPING_POLICY_VISITED = "shipping_policy_visited",
	PRIVACY_POLICY_VISITED = "privacy_policy_visited",
	CANCELLATION_REFUND_VISITED = "cancellation_refund_visited",

	// Eligibility
	ELIGIBILITY_VISITED = "eligibility_visited",

	// Referrals
	REFERRAL_PAGE_VISITED = "referral_page_visited",
	REFERRAL_TNC_VISITED = "referral_tnc_visited",
	REFERRAL_LINK_COPIED = "referral_link_copied",
	REFERRAL_CODE_COPIED = "referral_code_copied",
	SIGNUP_REFERRAL = "signup_referral",

	// Banners
	BANNER_CLICKED = "banner_clicked",
	BANNER_CTA_CLICKED = "banner_cta_clicked",

	// Buyback
	BUYBACK_ENROLLED = "buyback_enrolled",
	BUYBACK_REMOVED = "buyback_removed",

	// FDM (Fuel / Driver / Maintenance)
	FDM_ADD_CLICKED = "fdm_add_clicked",
	FDM_CONFIRMED = "fdm_confirmed",

	// Accessories
	ACCESSORY_REMOVED = "accessory_removed",
	ACCESSORY_DETAILS_VIEWED = "accessory_details_viewed",
	ACCESSORY_SEARCHED = "accessory_searched",
	ACCESSORY_SEARCH_RESULT_CLICKED = "accessory_search_result_clicked",

	// Filters (PLP + accessories) — distinguished by `source` param
	FILTERS_APPLIED = "filters_applied",

	// Insurance
	INSURANCE_ADDED = "insurance_added",
	INSURANCE_REMOVED = "insurance_removed",
}

export const trackEvent = (
	eventName: GTMEvent | string,
	params: Record<string, string | number | boolean | string[] | null> = {},
): void => {
	try {
		if (typeof window === "undefined") return;

		const userId = getUserId();

		const eventData = {
			event: eventName,
			app_user_id: userId,
			user_type: isAuthorized() ? "authorized" : "guest",
			page_path: window.location.pathname,
			...params,
		};

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(eventData);

		if (process.env.NODE_ENV === "development") {
			console.log(" GTM Event:", eventName, eventData);
		}

		// Push to CleverTap
		if (window.clevertap) {
			const cleanParams: Record<string, string | number | boolean> = {};

			Object.entries(eventData).forEach(([key, value]) => {
				if (key === "event") return;
				if (value !== null && value !== undefined && value !== "") {
					// Convert arrays to comma-separated strings
					if (Array.isArray(value)) {
						cleanParams[key] = value.join(", ");
					} else {
						cleanParams[key] = value;
					}
				}
			});

			// Only push if we have parameters
			if (Object.keys(cleanParams).length > 0) {
				window.clevertap.event.push(eventName, cleanParams);

				if (process.env.NODE_ENV === "development") {
					console.log("CleverTap Event:", eventName, cleanParams);
				}
			}
		} else if (process.env.NODE_ENV === "development") {
			console.log("CleverTap not loaded, skipping:", eventName);
		}
	} catch (error) {
		clientLogger.error(
			"Tracking error",
			error instanceof Error ? error : undefined,
		);
	}
};

// env var enabed for ct staging
